import type { Doc } from "building-types/doc";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { SerializedOptionSetEntityDisconnectedTable } from "building-types/options/optionSetEntityRelationsTypes";
import type { Mutable } from "common/utils/types";
import { applyRulesToOptionChoices } from "doc/options/applyRulesToOptionChoices";
import { sanitizeDocOptionChoices } from "doc/options/optionChoicesTools";
import type { OptionSetStateSet } from "doc/options/optionSetState";
import {
  evalOptionSetEntityRelations,
  optionSetEntityRelationsFromDoc,
} from "doc/options/relations/optionSetEntityRelations";

/**
 * Computes force-locked option choices that must be reset to defaults.
 */
export function computeShowroomSetToDefault(doc: Doc, optionChoices: OptionChoices): OptionChoices {
  const optionSetEntityRelations = optionSetEntityRelationsFromDoc(doc);
  const { setToDefault } = evalOptionSetEntityRelations({
    optionChoices,
    optionSetEntityRelations,
    doc,
  });
  return setToDefault;
}

/**
 * Applies showroom option preprocessing in one place:
 * merge previous/modified choices, then rules + sanitization (unless optionStateV2).
 */
export function prepareShowroomOptionChoices(
  doc: Doc,
  previousOptionChoices: OptionChoices,
  modifiedChoices: OptionChoices,
  optionStateV2: boolean = false,
): OptionChoices {
  const combinedChoices: OptionChoices = {
    ...previousOptionChoices,
    ...modifiedChoices,
  };

  if (optionStateV2) {
    return combinedChoices;
  }

  const choicesWithRules = applyRulesToOptionChoices(doc, combinedChoices, modifiedChoices);
  return sanitizeDocOptionChoices(doc, choicesWithRules);
}

/**
 * Combines force-lock resets and user-selected changed choices into a regeneration patch.
 *
 * Keyed on `OptionSetState.id` — the instance id for instance states, not the archetype — because
 * that is what the choice saves under.
 *
 * Inert for instances today: every producer sets `previousValue` to `currentValue`, so the diff
 * below never fires for them. See BDG-2971.
 */
export function getShowroomChangedChoicesForRegeneration(
  setToDefault: OptionChoices,
  optionSetStates: OptionSetStateSet,
  disconnectedSets: SerializedOptionSetEntityDisconnectedTable,
): OptionChoices {
  const changedChoices: Mutable<OptionChoices> = {
    ...setToDefault,
  };

  for (const optionSetState of Object.values(optionSetStates)) {
    const { id, currentValue, previousValue, optionSet } = optionSetState;
    // Locks stay on the archetype: one force-lock covers every instance.
    if (!optionSet.disabled && !disconnectedSets[optionSet.id] && currentValue !== previousValue) {
      changedChoices[id] = currentValue;
    }
  }

  return changedChoices;
}
